import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  api,
  mineralTitle,
  plural,
  type ReferenceStatus,
  type UnlinkedMineral,
} from '../api.ts';
import { Icon } from '../components/Icon.tsx';

/**
 * Справочник Mindat (ТЗ 8, спринт 7).
 *
 * Два дела на одном экране: наполнить справочник и разобрать то, что было
 * занесено свободным текстом до него. Второе важнее первого — пока
 * «Кварц» из спринта 2 живёт отдельной записью, поиск «все кварцы» его
 * не найдёт, а карточка не покажет ни формулы, ни твёрдости.
 */
export function ReferenceScreen() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<ReferenceStatus | null>(null);
  const [unlinked, setUnlinked] = useState<UnlinkedMineral[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const [next, list] = await Promise.all([api.referenceStatus(), api.unlinkedMinerals()]);
    setStatus(next);
    setUnlinked(list.unlinked);
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Пока выгрузка идёт, экран обновляется сам: это единственный способ
  // увидеть, что тысяча запросов к Mindat движется.
  useEffect(() => {
    if (status?.status !== 'running') return;
    const timer = setInterval(() => void api.referenceStatus().then(setStatus), 3000);
    return () => clearInterval(timer);
  }, [status?.status]);

  async function sync(only?: 'russian') {
    setBusy(true);
    setError(null);
    try {
      setStatus(await api.startReferenceSync(only));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось запустить');
    } finally {
      setBusy(false);
    }
  }

  async function relink() {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const result = await api.relinkMinerals();
      setNote(
        result.linked.length
          ? `Привязано: ${result.linked.map((l) => `${l.from} → ${l.to}`).join(', ')}`
          : 'Привязывать нечего: однозначных совпадений не нашлось',
      );
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось привязать');
    } finally {
      setBusy(false);
    }
  }

  async function link(id: string, targetId: string) {
    setBusy(true);
    try {
      await api.linkMineral(id, targetId);
      await reload();
    } finally {
      setBusy(false);
    }
  }

  const running = status?.status === 'running';

  return (
    <div className="app">
      <div className="topbar topbar--bordered">
        <button className="icon-btn" onClick={() => navigate(-1)} aria-label="Назад">
          <Icon name="back" className="icon muted" />
        </button>
        <div className="spacer" />
        <h2 className="topbar-title">Справочник</h2>
        <div className="spacer" />
        <span style={{ width: 20 }} />
      </div>

      <div className="scroll">
        <p className="small muted" style={{ marginTop: 'var(--space-4)' }}>
          Виды и разновидности берутся из Mindat и хранятся у вас на сервере.
          Поиск работает по локальной копии — и когда Mindat недоступен, и когда
          телефон в поле без связи.
        </p>

        <div className="stat-grid" style={{ marginTop: 'var(--space-3)' }}>
          <div className="stat">
            <b>{status?.species ?? '—'}</b>
            <span>видов</span>
          </div>
          <div className="stat">
            <b>{status?.varieties ?? '—'}</b>
            <span>разновидностей</span>
          </div>
        </div>
        <div className="hint">
          Русских названий: {status?.with_russian ?? 0}. Остальные показываются латиницей,
          как в Mindat.
        </div>

        {status && !status.token && (
          <div className="notice notice--warn" style={{ marginTop: 'var(--space-3)' }}>
            <Icon name="alert" className="icon icon-sm" />
            <span>Токен Mindat не задан на сервере — справочник можно только вводить руками.</span>
          </div>
        )}

        {running && (
          <div className="notice notice--ok" style={{ marginTop: 'var(--space-3)' }}>
            <Icon name="refresh" className="icon icon-sm" />
            <span>
              Идёт загрузка: {status.imported}{' '}
              {plural(status.imported, 'запись', 'записи', 'записей')}. Это больше тысячи
              запросов к Mindat, всё займёт минут десять. Экран можно закрыть.
            </span>
          </div>
        )}

        {status?.status === 'failed' && (
          <div className="notice notice--danger" style={{ marginTop: 'var(--space-3)' }}>
            <Icon name="alert" className="icon icon-sm" />
            <span>Загрузка прервалась: {status.message}. Можно запустить заново.</span>
          </div>
        )}

        {status?.status === 'done' && (
          <div className="hint">
            Последняя загрузка завершена, записей получено: {status.imported}
          </div>
        )}

        <button
          className="btn btn--full"
          style={{ marginTop: 'var(--space-3)' }}
          disabled={busy || running || !status?.token}
          onClick={() => void sync()}
        >
          <Icon name="download" className="icon icon-sm" />{' '}
          {running ? 'Загружается…' : status?.minerals ? 'Обновить справочник' : 'Загрузить справочник'}
        </button>

        {/* Отдельной кнопкой, потому что это отдельная работа: виды и
            разновидности уже лежат, а имена лежат у Mindat в синонимах —
            сорок восемь тысяч записей, из которых нужны кириллические. */}
        <button
          className="btn btn--full"
          style={{ marginTop: 'var(--space-2)' }}
          disabled={busy || running || !status?.token || !status?.minerals}
          onClick={() => void sync('russian')}
        >
          <Icon name="book" className="icon icon-sm" /> Дотянуть русские названия
        </button>
        <div className="hint">
          Обходит синонимы Mindat и подписывает ими виды. Идёт около часа, экран можно
          закрыть; поиск и ввод всё это время работают.
        </div>

        <div className="label-key">Непривязанные минералы</div>
        {unlinked === null ? (
          <div className="muted small">Загрузка…</div>
        ) : unlinked.length === 0 ? (
          <div className="notice notice--ok">
            <Icon name="check" className="icon icon-sm" />
            <span>Все минералы коллекции связаны со справочником.</span>
          </div>
        ) : (
          <>
            <div className="hint" style={{ marginTop: 0 }}>
              Эти названия заносились текстом до появления справочника. Свяжите их —
              и у образцов появятся формула, твёрдость и разновидности.
            </div>
            <button
              className="btn btn--full"
              style={{ marginTop: 'var(--space-3)' }}
              disabled={busy}
              onClick={relink}
            >
              Связать очевидные автоматически
            </button>
            {note && <div className="hint centered">{note}</div>}

            <div className="unlinked">
              {unlinked.map((item) => (
                <div key={item.id} className="unlinked-row">
                  <div className="unlinked-name">
                    {item.name}
                    <span className="hint">
                      {item.uses} {plural(item.uses, 'образец', 'образца', 'образцов')}
                    </span>
                  </div>
                  {item.candidates.length === 0 ? (
                    <div className="hint">
                      Похожего в справочнике нет. Поищите вручную в форме образца или
                      оставьте как есть — так тоже можно.
                    </div>
                  ) : (
                    <div className="chips">
                      {item.candidates.map((candidate) => (
                        <button
                          key={candidate.id}
                          className="chip chip--plain"
                          disabled={busy}
                          onClick={() => void link(item.id, candidate.id)}
                        >
                          {mineralTitle(candidate)}
                          {candidate.kind === 'variety' ? ' · разновидность' : ''}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {error && (
          <div className="notice notice--danger" style={{ marginTop: 'var(--space-3)' }}>
            {error}
          </div>
        )}

        <div className="hint" style={{ marginBottom: 'var(--space-6)' }}>
          Связывание необратимо только на бумаге: образец начинает ссылаться на запись
          справочника, а старое название остаётся в истории и в выгрузке.
        </div>
      </div>
    </div>
  );
}
